// Module-level session store. Single source of truth for the runtime's
// auth state. Replaces the previous pattern of holding `session` in a
// `useState` inside RuntimePage with an event-driven re-fetch, which
// left a window where stale session data was visible after logout.
//
// Writes go through `setSession` / `clearSession`. `useSession` reads
// via `useSyncExternalStore`.

import { useSyncExternalStore } from "react";
import type { RuntimeSession } from "../../runtime/types/core";

type Listener = (s: RuntimeSession) => void;

let current: RuntimeSession = null;
const listeners = new Set<Listener>();

// Per-user localStorage keys to wipe on session clear. The DataInput
// "already submitted" guard at `runtime/components/dataInput.tsx`
// writes `veltric-di-submitted-${userId}` and was previously orphaned
// across logout, leaving a same-userId follow-up user on a stuck
// "already submitted" UI. Add new prefixes here as user-scoped keys
// are introduced elsewhere in the runtime.
const USER_SCOPED_LOCALSTORAGE_PREFIXES = ["veltric-di-submitted-"];

export type ClearSessionReason = "expired" | "logout";

// Audit 2026-09-23 (4a): "your session expired" lives HERE and not in
// RuntimePage state, because the auth-changed remount that follows an
// expiry would otherwise throw the notice away with the component.
let sessionExpiredNotice = false;
const noticeListeners = new Set<() => void>();

// Re-audit 2026-09-23 (F-6): `Date.now()` of the last restored session
// (`setSession(non-null)`), or null once torn down. `api.ts` consults it so a
// 401 that lands right after the Worker's (Redis-cached) bootstrap restored a
// VALID session is not read as an expiry — that misread was a remount lap:
// clear ⇒ `wb:auth-changed` ⇒ remount ⇒ bootstrap restores ⇒ 401 ⇒ clear …
// Fix round 3 (H1): recorded INSIDE `setSession` — a restore IS a non-null
// set — instead of a separate call next to every App.tsx site (untested; both
// could be dropped with every test still green).
let lastSessionRestoredAt: number | null = null;

/** True when a session was set less than `ms` milliseconds ago. */
export function wasSessionRestoredWithin(ms: number): boolean {
  return (
    lastSessionRestoredAt !== null && Date.now() - lastSessionRestoredAt < ms
  );
}

export function getSession(): RuntimeSession {
  return current;
}

export function setSession(next: RuntimeSession): void {
  current = next;
  lastSessionRestoredAt = next !== null ? Date.now() : null;
  for (const l of listeners) l(next);
}

/**
 * Tear the local session down.
 *
 * Idempotent (audit 2026-09-23, 4a): `wb:auth-changed` fires only when there
 * WAS a session. It used to fire unconditionally, so an anonymous 401 (stale
 * `?appointmentId=` link, expired cookie) remounted the route, which
 * refetched, got 401 again, cleared again — and wiped the visitor's typed
 * form on every lap. The storage sweep below stays unconditional: it is
 * cheap, best-effort and cannot start a remount.
 */
export function clearSession(reason: ClearSessionReason = "logout"): void {
  const hadSession = current !== null;
  current = null;
  lastSessionRestoredAt = null;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (
        k &&
        USER_SCOPED_LOCALSTORAGE_PREFIXES.some((p) => k.startsWith(p))
      ) {
        localStorage.removeItem(k);
      }
    }
    // Symmetric cleanup for the email-link credential pre-fill so it
    // doesn't bridge across user identities on a shared device.
    sessionStorage.removeItem("_auth_prefill");
  } catch {
    /* SSR / restricted storage — best-effort */
  }
  if (!hadSession) return;
  if (reason === "expired") setSessionExpiredNotice(true);
  for (const l of listeners) l(null);
  try {
    window.dispatchEvent(
      new CustomEvent("wb:auth-changed", { detail: { reason } }),
    );
  } catch {
    /* SSR */
  }
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function setSessionExpiredNotice(next: boolean): void {
  if (sessionExpiredNotice === next) return;
  sessionExpiredNotice = next;
  for (const l of noticeListeners) l();
}

export function getSessionExpiredNotice(): boolean {
  return sessionExpiredNotice;
}

export function clearSessionExpiredNotice(): void {
  setSessionExpiredNotice(false);
}

function subscribeSessionExpiredNotice(fn: () => void): () => void {
  noticeListeners.add(fn);
  return () => {
    noticeListeners.delete(fn);
  };
}

/** True after a session was torn down because the server said 401. */
export function useSessionExpiredNotice(): boolean {
  return useSyncExternalStore(
    subscribeSessionExpiredNotice,
    getSessionExpiredNotice,
    getSessionExpiredNotice,
  );
}
